import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Tabs,
  Tab,
  InputGroup,
  Image as BSImage,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetWorkByIdQuery,
  useUpdateWorkMutation,
} from "../../../redux/api/WorksApi/worksApi";
import {
  FaTrash,
  FaArrowLeft,
  FaLayerGroup,
  FaInfoCircle,
  FaRocket,
  FaPlus,
  FaListUl,
  FaLink,
  FaEdit,
} from "react-icons/fa";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import DeleteModel from "../../../Components/DeleteModel";

const EditWork = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: workData, isLoading: isFetching } = useGetWorkByIdQuery(id);
  const [updateWork, { isLoading: isUpdating }] = useUpdateWorkMutation();

  const [activeTab, setActiveTab] = useState("general");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState({
    field: "",
    index: -1,
    label: "",
  });

  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    tagline: "",
    subtitle: "",
    overview_description: "",
    overview_goals_title: "",
    overview_summary: "",
    visual_mobile1: "",
    visual_mobile2: "",
    visual_laptop: "",
    client_goals_title: "",
    client_goals_image: "",
    key_challenges_title: "",
    key_challenges_image: "",
    solution_title: "",
    solution_description: "",
    results_title: "",
    results_description: "",
    website: "",
    websiteUrl: "",
    showDownloads: true,
    portalText: "",
    previewImage: "",
    ctaDescription: "",
    card_gradient: "",
    card_image: "",
    top_bg: "",
    btm_bg: "",
    playStoreUrl: "",
    appStoreUrl: "",
    status: true,
    display_order: 0,
    headerButtons: [],
    overviewGoals: [],
    clientGoalsList: [],
    keyChallengesList: [],
    solutionItems: [],
    resultsStats: [],
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
  });

  useEffect(() => {
    if (workData) {
      setFormData({
        ...workData,
        headerButtons: workData.headerButtons || [],
        overviewGoals: (workData.overviewGoals || []).map(
          (g) => g.text || (typeof g === "string" ? g : ""),
        ),
        clientGoalsList: (workData.clientGoalsList || []).map(
          (g) => g.title || (typeof g === "string" ? g : ""),
        ),
        keyChallengesList: (workData.keyChallengesList || []).map(
          (c) => c.title || (typeof c === "string" ? c : ""),
        ),
        solutionItems: workData.solutionItems || [],
        resultsStats: workData.resultsStats || [],
        card_image: workData.card_image || "",
        top_bg: workData.top_bg || "",
        btm_bg: workData.btm_bg || "",
        playStoreUrl: workData.playStoreUrl || "",
        appStoreUrl: workData.appStoreUrl || "",
        meta_title: workData.meta_title || "",
        meta_keyword: workData.meta_keyword || "",
        meta_description: workData.meta_description || "",
      });
    }
  }, [workData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e, field, index = null, subfield = null) => {
    const file = e.target.files[0];
    if (file) {
      if (index !== null && subfield) {
        setFormData((prev) => {
          const newArray = [...prev[field]];
          newArray[index] = { ...newArray[index], [subfield]: file };
          return { ...prev, [field]: newArray };
        });
      } else {
        setFormData((prev) => ({ ...prev, [field]: file }));
      }
    }
  };

  const getPreviewUrl = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file;
    return URL.createObjectURL(file);
  };

  const handleArrayChange = (field, index, value, subfield = null) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      if (subfield) newArray[index] = { ...newArray[index], [subfield]: value };
      else newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const addArrayItem = (field, defaultValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], defaultValue],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const confirmRemoveItem = (field, index, label) => {
    setDeleteConfig({ field, index, label });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    removeArrayItem(deleteConfig.field, deleteConfig.index);
    setShowDeleteModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    Object.keys(formData).forEach((key) => {
      if (
        ![
          "headerButtons",
          "overviewGoals",
          "clientGoalsList",
          "keyChallengesList",
          "solutionItems",
          "resultsStats",
          "visual_mobile1",
          "visual_mobile2",
          "visual_laptop",
          "client_goals_image",
          "key_challenges_image",
          "previewImage",
          "card_image",
          "work_buttons",
          "work_overview_goals",
          "work_client_goals",
          "work_key_challenges",
          "work_solution_items",
          "work_results_stats",
          "createdAt",
          "updatedAt",
          "id",
        ].includes(key)
      ) {
        data.append(key, formData[key]);
      }
    });

    data.append("headerButtons", JSON.stringify(formData.headerButtons));
    data.append("overviewGoals", JSON.stringify(formData.overviewGoals));
    data.append("clientGoalsList", JSON.stringify(formData.clientGoalsList));
    data.append(
      "keyChallengesList",
      JSON.stringify(formData.keyChallengesList),
    );
    data.append("resultsStats", JSON.stringify(formData.resultsStats));
    data.append(
      "solutionItems",
      JSON.stringify(
        formData.solutionItems.map((s) => ({
          ...s,
          icon: typeof s.icon === "string" ? s.icon : null,
        })),
      ),
    );

    const filesToAppend = [
      "visual_mobile1",
      "visual_mobile2",
      "visual_laptop",
      "client_goals_image",
      "key_challenges_image",
      "previewImage",
      "card_image",
    ];
    filesToAppend.forEach((f) => {
      if (formData[f] instanceof File) data.append(f, formData[f]);
    });

    formData.solutionItems.forEach((s, i) => {
      if (s.icon instanceof File) data.append(`solutionItems[${i}]`, s.icon);
    });

    try {
      await updateWork({ id, data }).unwrap();
      message.success("Work updated successfully!");
      navigate("/admin/works");
    } catch (error) {
      message.error(error.data?.error || "Failed to update work");
    }
  };

  if (isFetching || isUpdating)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );

  return (
    <div className="container mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-primary">
            <FaEdit className="me-2" />
            Edit Work
          </h2>
          <p className="text-muted">Modify project: {formData.title}</p>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() => navigate("/admin/works")}
        >
          <FaArrowLeft className="me-2" />
          Back
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Card className="border-0 shadow-sm overflow-hidden">
          <Card.Header className="bg-white p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              fill
              className="admin-tabs border-bottom-0"
            >
              <Tab
                eventKey="general"
                title={
                  <span>
                    <FaLayerGroup className="me-2" />
                    General
                  </span>
                }
              />
              <Tab
                eventKey="overview"
                title={
                  <span>
                    <FaInfoCircle className="me-2" />
                    Overview
                  </span>
                }
              />
              <Tab
                eventKey="goals"
                title={
                  <span>
                    <FaListUl className="me-2" />
                    Goals & Challenges
                  </span>
                }
              />
              <Tab
                eventKey="solution"
                title={
                  <span>
                    <FaRocket className="me-2" />
                    Solution
                  </span>
                }
              />
              <Tab
                eventKey="results"
                title={
                  <span>
                    <FaLink className="me-2" />
                    Results & Links
                  </span>
                }
              />
              <Tab
                eventKey="seo"
                title={
                  <span>
                    <FaRocket className="me-2" />
                    SEO Details
                  </span>
                }
              />
            </Tabs>
          </Card.Header>

          <Card.Body className="p-4">
            {activeTab === "general" && (
              <div className="fade-in">
                <Row className="g-3">
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Title</Form.Label>
                      <Form.Control
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Slug</Form.Label>
                      <Form.Control
                        name="slug"
                        value={formData.slug}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Tagline</Form.Label>
                      <Form.Control
                        name="tagline"
                        value={formData.tagline}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Subtitle</Form.Label>
                      <Form.Control
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Card Gradient</Form.Label>
                      <Form.Control
                        name="card_gradient"
                        value={formData.card_gradient}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Status</Form.Label>
                      <Form.Check
                        type="switch"
                        label="Active"
                        name="status"
                        checked={formData.status}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Order</Form.Label>
                      <Form.Control
                        type="number"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Card Image</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) => handleFileChange(e, "card_image")}
                      />
                      {formData.card_image && (
                        <BSImage
                          src={getPreviewUrl(formData.card_image)}
                          fluid
                          className="mt-2"
                          style={{ maxHeight: "100px" }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Top Background
                      </Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) => handleFileChange(e, "top_bg")}
                      />
                      {formData.top_bg && (
                        <BSImage
                          src={getPreviewUrl(formData.top_bg)}
                          fluid
                          className="mt-2"
                          style={{ maxHeight: "100px" }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Bottom Background
                      </Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) => handleFileChange(e, "btm_bg")}
                      />
                      {formData.btm_bg && (
                        <BSImage
                          src={getPreviewUrl(formData.btm_bg)}
                          fluid
                          className="mt-2"
                          style={{ maxHeight: "100px" }}
                        />
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <h5 className="mt-4 text-secondary">Header Buttons</h5>
                {formData.headerButtons.map((btn, i) => (
                  <Row key={i} className="mb-2 align-items-end g-2">
                    <Col md={5}>
                      <Form.Control
                        placeholder="Text"
                        value={btn.text}
                        onChange={(e) =>
                          handleArrayChange(
                            "headerButtons",
                            i,
                            e.target.value,
                            "text",
                          )
                        }
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Control
                        placeholder="Link"
                        value={btn.link}
                        onChange={(e) =>
                          handleArrayChange(
                            "headerButtons",
                            i,
                            e.target.value,
                            "link",
                          )
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        variant="outline-danger"
                        onClick={() => removeArrayItem("headerButtons", i)}
                      >
                        <FaTrash />
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    addArrayItem("headerButtons", { text: "", link: "" })
                  }
                >
                  <FaPlus /> Add Button
                </Button>
              </div>
            )}

            {activeTab === "seo" && (
              <div className="fade-in">
                <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
                  SEO Meta Details
                </h5>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Meta Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        placeholder="Enter SEO meta title..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Meta Keywords</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="meta_keyword"
                        value={formData.meta_keyword}
                        onChange={handleChange}
                        placeholder="keyword1, keyword2, keyword3..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">
                        Meta Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        placeholder="Enter SEO meta description..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === "overview" && (
              <div className="fade-in">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="overview_description"
                    value={formData.overview_description}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Goals List Title</Form.Label>
                  <Form.Control
                    name="overview_goals_title"
                    value={formData.overview_goals_title}
                    onChange={handleChange}
                  />
                </Form.Group>

                <h6 className="text-secondary">Goals List Items</h6>
                {formData.overviewGoals.map((g, i) => (
                  <InputGroup key={i} className="mb-2">
                    <Form.Control
                      value={g}
                      onChange={(e) =>
                        handleArrayChange("overviewGoals", i, e.target.value)
                      }
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => confirmRemoveItem("overviewGoals", i, g)}
                    >
                      <FaTrash />
                    </Button>
                  </InputGroup>
                ))}
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => addArrayItem("overviewGoals", "")}
                >
                  <FaPlus /> Add Goal
                </Button>

                <Form.Group className="mt-3">
                  <Form.Label className="fw-bold">Summary Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="overview_summary"
                    value={formData.overview_summary}
                    onChange={handleChange}
                  />
                </Form.Group>

                <h5 className="mt-4 text-secondary">Visuals (Images)</h5>
                <Row>
                  {["visual_mobile1", "visual_mobile2", "visual_laptop"].map(
                    (f) => (
                      <Col md={4} key={f}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-capitalize">
                            {f.replace("_", " ")}
                          </Form.Label>
                          <Form.Control
                            type="file"
                            onChange={(e) => handleFileChange(e, f)}
                          />
                          {formData[f] && (
                            <BSImage
                              src={getPreviewUrl(formData[f])}
                              fluid
                              className="mt-2"
                              style={{ maxHeight: "100px" }}
                            />
                          )}
                        </Form.Group>
                      </Col>
                    ),
                  )}
                </Row>
              </div>
            )}

            {activeTab === "goals" && (
              <div className="fade-in">
                <Row>
                  <Col md={6}>
                    <h5 className="text-primary">Client Goals</h5>
                    <Form.Group className="mb-2">
                      <Form.Label>Goals Title</Form.Label>
                      <Form.Control
                        name="client_goals_title"
                        value={formData.client_goals_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Goals Image</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) =>
                          handleFileChange(e, "client_goals_image")
                        }
                      />
                    </Form.Group>
                    {formData.client_goals_image &&
                      typeof formData.client_goals_image === "string" && (
                        <BSImage
                          src={getPreviewUrl(formData.client_goals_image)}
                          width={100}
                          className="mb-2"
                        />
                      )}
                    {formData.clientGoalsList.map((g, i) => (
                      <InputGroup key={i} className="mb-2">
                        <Form.Control
                          value={g}
                          onChange={(e) =>
                            handleArrayChange(
                              "clientGoalsList",
                              i,
                              e.target.value,
                            )
                          }
                        />
                        <Button
                          variant="outline-danger"
                          onClick={() =>
                            confirmRemoveItem("clientGoalsList", i, g)
                          }
                        >
                          <FaTrash />
                        </Button>
                      </InputGroup>
                    ))}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => addArrayItem("clientGoalsList", "")}
                    >
                      <FaPlus /> Add Goal
                    </Button>
                  </Col>
                  <Col md={6}>
                    <h5 className="text-danger">Key Challenges</h5>
                    <Form.Group className="mb-2">
                      <Form.Label>Challenges Title</Form.Label>
                      <Form.Control
                        name="key_challenges_title"
                        value={formData.key_challenges_title}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Challenges Image</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) =>
                          handleFileChange(e, "key_challenges_image")
                        }
                      />
                    </Form.Group>
                    {formData.key_challenges_image &&
                      typeof formData.key_challenges_image === "string" && (
                        <BSImage
                          src={getPreviewUrl(formData.key_challenges_image)}
                          width={100}
                          className="mb-2"
                        />
                      )}
                    {formData.keyChallengesList.map((c, i) => (
                      <InputGroup key={i} className="mb-2">
                        <Form.Control
                          value={c}
                          onChange={(e) =>
                            handleArrayChange(
                              "keyChallengesList",
                              i,
                              e.target.value,
                            )
                          }
                        />
                        <Button
                          variant="outline-danger"
                          onClick={() =>
                            confirmRemoveItem("keyChallengesList", i, c)
                          }
                        >
                          <FaTrash />
                        </Button>
                      </InputGroup>
                    ))}
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => addArrayItem("keyChallengesList", "")}
                    >
                      <FaPlus /> Add Challenge
                    </Button>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === "solution" && (
              <div className="fade-in">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Solution Title</Form.Label>
                  <Form.Control
                    name="solution_title"
                    value={formData.solution_title}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Solution Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="solution_description"
                    value={formData.solution_description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <h5 className="text-secondary">Solution Items</h5>
                {formData.solutionItems.map((item, i) => (
                  <Card
                    key={i}
                    className="mb-3 bg-light border-0 shadow-sm p-3"
                  >
                    <Row className="g-2">
                      <Col md={4}>
                        <Form.Control
                          placeholder="Title"
                          value={item.title}
                          onChange={(e) =>
                            handleArrayChange(
                              "solutionItems",
                              i,
                              e.target.value,
                              "title",
                            )
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Control
                          type="file"
                          onChange={(e) =>
                            handleFileChange(e, "solutionItems", i, "icon")
                          }
                        />
                      </Col>
                      <Col md={4} className="d-flex align-items-center">
                        {item.icon && (
                          <BSImage
                            src={getPreviewUrl(item.icon)}
                            width={30}
                            className="me-2"
                          />
                        )}{" "}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            confirmRemoveItem("solutionItems", i, item.title)
                          }
                        >
                          <FaTrash />
                        </Button>
                      </Col>
                      <Col md={12}>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) =>
                            handleArrayChange(
                              "solutionItems",
                              i,
                              e.target.value,
                              "description",
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    addArrayItem("solutionItems", {
                      title: "",
                      description: "",
                      icon: "",
                    })
                  }
                >
                  <FaPlus /> Add Item
                </Button>
              </div>
            )}

            {activeTab === "results" && (
              <div className="fade-in">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Results Section Title
                  </Form.Label>
                  <Form.Control
                    name="results_title"
                    value={formData.results_title}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Results Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="results_description"
                    value={formData.results_description}
                    onChange={handleChange}
                  />
                </Form.Group>

                <h5 className="text-secondary">Stats</h5>
                <Row className="g-2">
                  {formData.resultsStats.map((stat, i) => (
                    <Col md={6} key={i}>
                      <Card className="p-2 mb-2">
                        <Row className="g-1 align-items-center">
                          <Col xs={4}>
                            <Form.Control
                              placeholder="Value"
                              value={stat.value}
                              onChange={(e) =>
                                handleArrayChange(
                                  "resultsStats",
                                  i,
                                  e.target.value,
                                  "value",
                                )
                              }
                            />
                          </Col>
                          <Col xs={7}>
                            <Form.Control
                              placeholder="Label"
                              value={stat.label}
                              onChange={(e) =>
                                handleArrayChange(
                                  "resultsStats",
                                  i,
                                  e.target.value,
                                  "label",
                                )
                              }
                            />
                          </Col>
                          <Col xs={1}>
                            <Button
                              variant="link"
                              className="text-danger p-0"
                              onClick={() => removeArrayItem("resultsStats", i)}
                            >
                              <FaTrash />
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() =>
                    addArrayItem("resultsStats", {
                      value: "",
                      label: "",
                    })
                  }
                >
                  <FaPlus /> Add Stat
                </Button>

                <hr />
                <h5 className="text-secondary">Footer & Links</h5>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Website Name</Form.Label>
                      <Form.Control
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Website URL</Form.Label>
                      <Form.Control
                        name="websiteUrl"
                        value={formData.websiteUrl}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Preview Image</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) => handleFileChange(e, "previewImage")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Portal Text</Form.Label>
                      <Form.Control
                        name="portalText"
                        value={formData.portalText}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-2">
                      <Form.Label>CTA Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="ctaDescription"
                        value={formData.ctaDescription}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>Play Store URL</Form.Label>
                      <Form.Control
                        name="playStoreUrl"
                        value={formData.playStoreUrl}
                        onChange={handleChange}
                        placeholder="https://play.google.com/store/apps/details?id=..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Label>App Store URL</Form.Label>
                      <Form.Control
                        name="appStoreUrl"
                        value={formData.appStoreUrl}
                        onChange={handleChange}
                        placeholder="https://apps.apple.com/app/..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}
          </Card.Body>

          <Card.Footer className="bg-white p-4 text-end">
            <Button
              variant="success"
              type="submit"
              size="lg"
              className="px-5 rounded-pill"
            >
              <FaEdit className="me-2" /> Update Work
            </Button>
          </Card.Footer>
        </Card>
      </Form>

      <DeleteModel
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeleteConfirm}
        label={deleteConfig.label}
      />
    </div>
  );
};

export default EditWork;
